<template>
  <v-card
    variant="outlined"
    class="pa-4 d-flex flex-column"
    style="border-radius: 16px"
  >
    <div class="d-flex align-baseline justify-space-between mb-2">
      <div>
        <div class="text-subtitle-2 font-weight-bold">{{ titulo }}</div>
        <div v-if="subtitulo" class="text-caption text-muted">
          {{ subtitulo }}
        </div>
      </div>
      <div
        v-if="mostrarTotal && totalMostrado !== null"
        class="text-h6 font-weight-bold"
        :style="{ color: color }"
      >
        {{ totalMostrado }}
      </div>
    </div>

    <div v-if="!valores || valores.length === 0" class="text-caption text-muted text-center py-8">
      Sin datos para este período
    </div>

    <template v-else>
      <svg
        :viewBox="`0 0 ${anchoSvg} ${altoSvg}`"
        preserveAspectRatio="none"
        :style="{ width: '100%', height: altura + 'px' }"
      >
        <defs>
          <linearGradient :id="gradientId" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" :stop-color="color" stop-opacity="0.28" />
            <stop offset="100%" :stop-color="color" stop-opacity="0" />
          </linearGradient>
        </defs>

        <template v-if="tipo === 'area'">
          <path
            :d="pathArea"
            :fill="`url(#${gradientId})`"
            stroke="none"
          />
          <path
            :d="pathLinea"
            fill="none"
            :stroke="color"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <circle
            v-if="puntos.length"
            :cx="puntos[puntos.length - 1].x"
            :cy="puntos[puntos.length - 1].y"
            r="5"
            :fill="color"
          />
        </template>

        <template v-else>
          <rect
            v-for="(barra, i) in barras"
            :key="i"
            :x="barra.x"
            :y="barra.y"
            :width="barra.ancho"
            :height="barra.alto"
            rx="5"
            :fill="barra.destacada ? color : colorClaro"
          />
        </template>
      </svg>

      <div class="d-flex justify-space-between text-caption text-muted px-1 mt-1">
        <span v-for="(etiqueta, i) in etiquetas" :key="i">{{ etiqueta }}</span>
      </div>
    </template>
  </v-card>
</template>
<script>
export default {
  name: "GraficoTendencia",

  props: {
    etiquetas: { type: Array, default: () => [] },
    valores: { type: Array, default: () => [] },
    color: { type: String, default: "#4682B4" },
    colorClaro: { type: String, default: "#EAF2F8" },
    titulo: { type: String, required: true },
    subtitulo: { type: String, default: "" },
    tipo: { type: String, default: "area" }, // 'area' | 'barra'
    altura: { type: Number, default: 170 },
    mostrarTotal: { type: Boolean, default: true },
    total: { type: [Number, String], default: null },
  },

  data: () => ({
    anchoSvg: 600,
    altoSvg: 190,
    paddingX: 12,
    paddingTop: 14,
    paddingBottom: 18,
  }),

  computed: {
    gradientId() {
      return "grad-" + Math.random().toString(36).slice(2, 9);
    },

    totalMostrado() {
      if (this.total !== null) return this.total;
      if (!this.valores || this.valores.length === 0) return null;
      const suma = this.valores.reduce((acc, v) => acc + (Number(v) || 0), 0);
      return suma;
    },

    innerWidth() {
      return this.anchoSvg - this.paddingX * 2;
    },

    innerHeight() {
      return this.altoSvg - this.paddingTop - this.paddingBottom;
    },

    puntos() {
      const n = this.valores.length;
      if (n === 0) return [];
      const min = Math.min(...this.valores);
      const max = Math.max(...this.valores);
      const rango = max - min || 1;
      const stepX = n > 1 ? this.innerWidth / (n - 1) : 0;
      return this.valores.map((valor, i) => ({
        x: this.paddingX + i * stepX,
        y:
          this.paddingTop +
          this.innerHeight -
          ((valor - min) / rango) * this.innerHeight,
      }));
    },

    pathLinea() {
      if (this.puntos.length === 0) return "";
      return this.puntos
        .map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`)
        .join(" ");
    },

    pathArea() {
      if (this.puntos.length === 0) return "";
      const base = this.altoSvg - this.paddingBottom;
      const primero = this.puntos[0];
      const ultimo = this.puntos[this.puntos.length - 1];
      return `${this.pathLinea} L${ultimo.x},${base} L${primero.x},${base} Z`;
    },

    barras() {
      const n = this.valores.length;
      if (n === 0) return [];
      const max = Math.max(...this.valores, 1);
      const gap = 14;
      const ancho = Math.min((this.innerWidth - gap * (n - 1)) / n, 42);
      const base = this.altoSvg - this.paddingBottom;
      return this.valores.map((valor, i) => {
        const alto = (valor / max) * this.innerHeight;
        return {
          x: this.paddingX + i * (ancho + gap),
          y: base - alto,
          ancho,
          alto,
          destacada: valor >= max * 0.75,
        };
      });
    },
  },
};
</script>
